import React, { useEffect, useState, useRef } from 'react';
import { getCheckActiveMarginJobsStatus } from 'app/services/SharedServices/SharedServices';
import { keyframes } from '@emotion/react';
import { styled } from '@mui/material/styles';

interface Props {
  orderId: number;
  interval?: number; // defaults to 20 000 ms
}

/* --- Inner bubble pulse --- */
const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.9; }
  50% { transform: scale(1.4); opacity: 1; }
  100% { transform: scale(1); opacity: 0.9; }
`;

/* --- Outer ripple --- */
const ripple = keyframes`
  0% { transform: scale(1); opacity: 0.4; }
  70% { transform: scale(2.5); opacity: 0.1; }
  100% { transform: scale(3); opacity: 0; }
`;

/* --- Styled bubble --- */
const StatusBubble = styled('span')<{ color: string; animated?: boolean }>(({ color, animated }) => ({
    position: 'relative',
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginLeft: 6,
    backgroundColor: color,
    transition: 'background-color 0.3s ease',
    animation: animated ? `${pulse} 1.5s infinite ease-in-out` : 'none',
    boxShadow: animated ? `0 0 6px ${color}` : 'none',
    overflow: 'visible',
  
    '&::after': animated
      ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          backgroundColor: color,
          transformOrigin: 'center',
          opacity: 0.4,
          animation: `${ripple} 2s infinite ease-out`,
          pointerEvents: 'none',
          zIndex: -1,
        }
      : {},
  }));  

export const CheckActiveMarginStatus: React.FC<Props> = ({ orderId, interval = 20000 }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [wasRunning, setWasRunning] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = async () => {
    try {
      const res = await getCheckActiveMarginJobsStatus(orderId);
      const currentStatus = !!res?.data;
      setIsRunning(currentStatus);

      // Remember if job ever started
      if (currentStatus && !wasRunning) setWasRunning(true);
    } catch (error) {
      console.error('Error fetching margin job status:', error);
    }
  };

  useEffect(() => {
    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, interval);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [orderId]);
  
  let color = '#9e9e9e';
  let tooltip = 'No recent updates to Order Pay Packages margin values.';
  let animated = false;

  if (isRunning) {
    color = 'orange';
    tooltip = 'A job is currently running to update Order Pay Packages margin values.';
    animated = true;
  } else if (!isRunning && wasRunning) {
    color = '#2196f3'; // blue
    tooltip = 'There is an update to Order Pay Packages margin values.\nPlease refresh to view the latest details.';
    animated = true;
  }

  return <StatusBubble color={color} animated={animated} title={tooltip} />;
};
